import { useIsFocused, useNavigation } from '@react-navigation/native'
import { StackScreenProps } from '@react-navigation/stack'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { StyleSheet, View } from 'react-native'
import 'react-native-get-random-values'
import { IconButton, Text } from 'react-native-paper'
import Toast from 'react-native-root-toast'
import Row from '../../../../@ui/containers/grid/row'
import EagleView from '../../replay/shared/eagle-view'
import { ScreenWrapper } from '../../../../@ui/containers/grid/screen.wrapper'
import { useHeaderButton } from '../../../../contexts/header-button.provider'
import {
  ArticleQuery,
  ContributionType,
  TargetType,
  useArticleQuery,
} from '../../../../generated/graphql'
import { useWechatShareInit } from '../../../../hooks/wechat-share.hook'
import { AppNavigatorParams, AppNavigation } from '../../../../navigation/types'
import { SocketEvents, useSocketRoom } from '../../../../socket/socket.hooks'
import { ERROR_CODES, hasApolloErrorCode } from '../../../../utils/error-utils'
import { Article, Contribution } from './types'
import { Tutorial } from '../../../../@ui/containers/others/tutorial/tutorial'
import { TutorialKey } from '../../../../@ui/containers/others/tutorial/tutorial.storage'
import { createStyle } from '../../../../contexts/global-styles.provider'
import { ContributionsButtonsBar } from './components/contribution.footer'
import {
  CreateTaskModal,
  CreateTaskModalProps,
} from '../../../tasks/components/create-task.modal'
import { ContributionInput } from './contribution-input/contribution.input'
import { DefaultBar } from './components/default.footer'
import { LevelIndicator } from '../../clusters/shared/LevelIndicator'
import {
  DisplayPerspective,
  DisplayPerspectiveRef,
} from './display-perspective'
import {
  PerspectiveSettingsModalRefs,
  PerspectiveSettingsModalWithAdvanceSettings,
} from './perspective-settings.modal'
import { useAuthContext } from '../../../../contexts/auth-context'
import { CorankTag } from '../../../../@ui'
import { LLMProvider } from '../create-article/llm/llm.provider'
import { TaskPendingIcon } from '../../../tasks/components/task-pending-icon'
import { MelddTaskTargetType } from '../../../tasks/task.types'
import { MessageType } from '../../../../utils/message-type'
import { VoteButton } from '../../clusters/cluster-cards/vote/vote.button'

export type ArticleDetails = ArticleQuery['article']

const PerspectiveContributionTutorial = () => {
  const { t } = useTranslation('tutorials')
  const steps = useMemo(
    () => [{ tutorialId: 'article_editor', message: t('add_contribution') }],
    [t]
  )

  return <Tutorial steps={steps} tutorialKey={TutorialKey.PERSPECTIVE_SCREEN} />
}

function PerspectiveScreenImplementation({ articleId }: { articleId: string }) {
  /**
   * Basics hooks
   */
  const navigation = useNavigation<AppNavigation>()
  const { userId, isAnonymous, openLoginModal, authAction } = useAuthContext()
  const { t } = useTranslation('articles')
  const isFocused = useIsFocused()
  const styles = useStyles()
  const { meta: mode, setMeta: setMode } = useHeaderButton<
    'history-mode' | 'read-mode'
  >('perspectiveMode')
  /**
   * Refs
   */
  const createTaskModalRef = useRef<CreateTaskModalProps>(null)
  const displayPerspectiveRef = useRef<DisplayPerspectiveRef>(null)
  /**
   * To Control the Header menus
   */
  const { setHandler: setCreateTaskModalAction } =
    useHeaderButton<boolean>('createTask')
  const { setMeta: setMetaPerspective } =
    useHeaderButton<Article>('perspective')
  const { meta: eagleViewOpen, setMeta: setEagleViewOpen } =
    useHeaderButton<boolean>('eagleView')
  const { setHandler: setBackButton } = useHeaderButton('navigation')

  /**
   * Contribution tht we are editing, is saved on the backend
   */
  const [reservedContribution, setReservedContribution] =
    useState<Contribution | null>(null)
  const [contributingLayoutOffset, setContributingLayoutOffset] = useState(0)
  /**
   * State of the screen
   */
  const [selection, setSelection] = useState({
    start: 0,
    end: 0,
    html: '',
  })
  const [perspective, setPerspective] = useState<Article | undefined>(undefined)
  const settingsModalRef = useRef<PerspectiveSettingsModalRefs>(null)

  /**
   * API Calls
   */
  const {
    data,
    refetch: refetchCore,
    error: articleError,
  } = useArticleQuery({
    variables: { id: articleId },
    skip: !articleId,
    fetchPolicy: 'cache-first',
  })

  const refetch = useCallback(async () => {
    await refetchCore()
  }, [refetchCore])

  useSocketRoom(articleId).event(SocketEvents.Refresh, () => {
    console.log('refresh')
    refetch()
  })

  const contributions: Contribution[] = useMemo(() => {
    return data?.article.contributions || []
  }, [data?.article])

  useEffect(() => {
    if (!articleError) {
      return
    }

    // For some scenarios request login
    if (
      hasApolloErrorCode(articleError, [
        ERROR_CODES.METHOD_NOT_ALLOWED,
        ERROR_CODES.FORBIDDEN,
      ]) &&
      isAnonymous
    ) {
      openLoginModal(navigation)
      return
    }
    // Display the error
    Toast.show(
      t(articleError?.message || "We couldn't find your article"),
      MessageType.error
    )
  }, [articleError])

  useEffect(() => {
    if (!perspective) {
      return
    }
    setCreateTaskModalAction(() =>
      authAction(navigation, () => {
        createTaskModalRef.current?.open(
          perspective.id,
          MelddTaskTargetType.ARTICLE_TASK,
          perspective.title
        )
      })
    )
  }, [perspective])

  useEffect(() => {
    if (
      typeof window !== 'undefined' &&
      new URLSearchParams(window.location.search).get('eagle-view') === 'true'
    ) {
      setEagleViewOpen(true)
    }
  }, [])

  useEffect(() => {
    if (isFocused) {
      refetch()
    }

    // Start with set visible false.
    // Visible stays in a reducer, thats why
    // We need to reset each new screen
    setMode('read-mode')
  }, [isFocused])

  useEffect(() => {
    if (!data?.article) {
      return
    }
    setPerspective(data.article)
    setMetaPerspective(data.article)
  }, [data?.article])

  useEffect(() => {
    if (!perspective?.clusterId) {
      return
    }
    setBackButton(() => {
      navigation.push('ClusterCards', {
        clusterId: perspective.clusterId,
        nodeId: perspective.cluster.nodeId,
      })
    })
  }, [perspective?.clusterId])

  useEffect(() => {
    if (!contributions?.length) {
      return
    }

    // IF I have an active contribution
    const reservedContribution = contributions.find(
      (c) =>
        c.userId == userId &&
        c.contributionType === ContributionType.Reserved &&
        new Date(c.reservedUntil).getTime() > Date.now()
    )

    if (reservedContribution) {
      setMode('history-mode')
      setReservedContribution(reservedContribution)
      displayPerspectiveRef.current?.lockSelection({
        start: reservedContribution.begin,
        end: reservedContribution.end,
        html: reservedContribution.originalText,
      })
      return
    }

    setReservedContribution(null)
    setSelection({ start: 0, end: 0, html: '' })
  }, [contributions, setReservedContribution])

  useWechatShareInit(() => {
    if (!perspective) return null

    const contributionCount = perspective.contributions.length
    const voteCount = perspective.voteCount
    const hasContributions = contributionCount > 0
    const hasVotes = voteCount > 0

    if (!hasContributions || !hasVotes) {
      return {
        title: perspective.title,
      }
    }

    return {
      title: perspective.title,
      description: `${contributionCount} contribution(s) and ${voteCount} vote(s)`,
    }
  }, [perspective])

  const textSelected = selection.end - selection.start > 0
  const historyMode = mode === 'history-mode'

  // All the bottom banners are fixed on the bottom but we need to push
  // a margin to allow the text to be scrollable til the bottom
  const editorPaddingBottom = useMemo(() => {
    // The differen editor inputs have different heights
    if (reservedContribution) {
      return contributingLayoutOffset
    }

    if (historyMode) {
      return 8
    }

    if (contributingLayoutOffset) {
      return contributingLayoutOffset
    }
    // Case of default bar OR input buttons
    return 74
  }, [historyMode, reservedContribution, contributingLayoutOffset])

  if (!isFocused) {
    return null
  }

  return (
    <ScreenWrapper withScrollView={false}>
      {perspective && !historyMode && (
        <Row
          style={{ paddingHorizontal: 24, paddingTop: 8, paddingBottom: 12 }}
          justifyContent="space-between"
        >
          <Row alignItems="center" columnGap={4}>
            <TaskPendingIcon
              targetId={perspective.id}
              targetType={MelddTaskTargetType.ARTICLE_TASK}
              size={22}
            />
          </Row>

          <Row alignItems="center" columnGap={4}>
            <LevelIndicator
              targetId={perspective.id}
              target={TargetType.Article}
              onEagleViewClick={() => setEagleViewOpen(!eagleViewOpen)}
              eagleViewOpen={eagleViewOpen}
            />
            <VoteButton
              perspective={perspective}
              mode="horizontal"
              style={{ marginRight: 12 }}
            />
          </Row>
        </Row>
      )}
      {perspective && !reservedContribution && (
        <Row
          style={{
            paddingHorizontal: 24,
            paddingBottom: 10,
          }}
          alignItems="center"
        >
          <Text variant={'headlineSmall'} style={{ marginRight: 12 }}>
            {perspective.title}
          </Text>
          <CorankTag corank={perspective.minCoRank} />
          {perspective.isOwner && (
            <IconButton
              icon="cog-outline"
              onPress={() => settingsModalRef.current?.open(perspective.id)}
              accessibilityLabel="Open Perspective Settings"
            />
          )}{' '}
        </Row>
      )}

      <View
        style={{
          flex: 1,
          // The bottom bars are fix, we need a margin to be able tos scroll to the bottom
          paddingBottom: editorPaddingBottom,
        }}
      >
        <DisplayPerspective
          ref={displayPerspectiveRef}
          key={articleId}
          perspective={perspective}
          contributions={contributions}
          displayVerticalIndicator={historyMode}
          verticalIndicatorOffset={!!reservedContribution}
          refetch={refetch}
          setSelection={setSelection}
          onContribution={() => setMode('history-mode')}
          onFullScreen={(contributionId) =>
            navigation.navigate('PerspectiveContribution', {
              contributionId,
            })
          }
        />
      </View>
      <>
        {/*
          Normally this is only access through MButton, however in this screen
          we open the modal from the menu, that doesn't allow appollo component
          (cos a bug that I don't understand) on <header>subcomponents</header>
        */}
        {perspective && <CreateTaskModal ref={createTaskModalRef} />}

        {perspective && (
          <ContributionInput
            context={perspective?.latestContent || ''}
            contribution={reservedContribution}
            onLayoutOffset={(contributionLayoutOffset) =>
              setContributingLayoutOffset(contributionLayoutOffset)
            }
            onClose={() => {
              setReservedContribution(null)
              displayPerspectiveRef.current?.clearSelection()
              refetch()
            }}
          />
        )}
        {perspective && textSelected && !reservedContribution && (
          <ContributionsButtonsBar
            perspective={perspective}
            selection={selection}
            setReservedContribution={setReservedContribution}
          />
        )}

        {perspective && (
          <EagleView
            targetId={perspective.id}
            open={eagleViewOpen}
            onClose={() => setEagleViewOpen(false)}
            shareParams={{
              targetType: TargetType.Article,
              targetId: articleId,
            }}
          />
        )}
        {perspective &&
          !historyMode &&
          !reservedContribution &&
          !textSelected &&
          !eagleViewOpen && (
            <DefaultBar
              articleId={perspective?.id}
              owner={perspective?.owner || { id: '', username: '' }}
            />
          )}
        <PerspectiveContributionTutorial />
      </>
      <PerspectiveSettingsModalWithAdvanceSettings
        ref={settingsModalRef}
        perspectiveId={perspective?.id || ''}
        onClose={() => {
          settingsModalRef.current?.close()
        }}
      />
    </ScreenWrapper>
  )
}

export default function PerspectiveScreen({
  route,
}: StackScreenProps<AppNavigatorParams, 'Perspective'>) {
  const { articleId } = route.params
  /*
   * React Native reuses the page component,
   * causing a cache effect and displaying previous article content.
   *
   * This is a workaround to force a new component to be created when the articleId changes.
   */
  return (
    <LLMProvider>
      <PerspectiveScreenImplementation key={articleId} articleId={articleId} />
    </LLMProvider>
  )
}

const useStyles = createStyle(({ theme }) =>
  StyleSheet.create({
    editor: {
      flex: 1,
      flexShrink: 1,
      backgroundColor: 'white',
      paddingHorizontal: theme.spacing(2),
    },
    info: {
      paddingVertical: theme.spacing(4),
      paddingHorizontal: theme.spacing(3),
    },
  })
)
